import {
  ElementDrafts,
  Template,
  TemplateElement,
  TemplatePreview,
  TemplateRoleTags,
} from '../models/templates.types';
import isEqual from 'lodash.isequal';
import { Option } from '../../../shared/models/shared.types';
import { WritableDraft } from 'immer';

const TEMPLATE_CACHE_KEY = '__store_template_cached';
const WORKBENCH_CACHE_KEY = '__store_workbench_template_previews';

export const saveTemplateToCache = (
  userId: string | undefined,
  template: Template | undefined | null
) => {
  if (!userId || !template) return;
  localStorage.setItem(
    `${TEMPLATE_CACHE_KEY}_${userId}_${template.id}`,
    JSON.stringify(template)
  );
};

export const loadTemplateFromCache = (
  userId: string | undefined,
  templateId: string | undefined
): Template | null => {
  if (!userId || !templateId) return null;
  const cacheStr = localStorage.getItem(
    `${TEMPLATE_CACHE_KEY}_${userId}_${templateId}`
  );
  const cached: Template = cacheStr ? JSON.parse(cacheStr) : null;
  return cached;
};

export const saveTemplatePreviewsToCache = (
  userId: string | undefined,
  templates: TemplatePreview[]
) => {
  if (!userId) return;

  try {
    localStorage.setItem(
      `${WORKBENCH_CACHE_KEY}_${userId}`,
      JSON.stringify(templates ?? [])
    );
  } catch (e) {
    console.warn('Failed to save templates to localStorage', e);
  }
};

export const loadTemplatePreviewsFromCache = (
  userId: string | undefined
): TemplatePreview[] => {
  if (!userId) return [];

  try {
    const cacheStr = localStorage.getItem(`${WORKBENCH_CACHE_KEY}_${userId}`);
    const cached = cacheStr ? JSON.parse(cacheStr) : [];

    return cached;
  } catch (e) {
    console.warn('Failed to load templates from localStorage', e);
    return [];
  }
};

export const isTemplatePreviewsDifferent = (
  userId: string | undefined,
  incoming: TemplatePreview[]
): boolean => {
  if (!userId) return true;

  try {
    const cacheStr = localStorage.getItem(`${WORKBENCH_CACHE_KEY}_${userId}`);
    const cached: TemplatePreview[] = cacheStr ? JSON.parse(cacheStr) : [];

    return !isEqual(cached, incoming);
  } catch (e) {
    console.warn('Failed to compare templates from cache', e);
    return true;
  }
};

export const saveElementCreateEditToCache = (
  storage_key: string,
  new_element: TemplateElement
) => {
  // Check currently saved draft
  const draftKey = `__store_element-drafts_${storage_key}`;
  const editFlagKey = `__store_template-section-element-edit_${storage_key}`;

  const existingDraftRaw = localStorage.getItem(draftKey);
  let updatedDrafts: ElementDrafts;

  if (existingDraftRaw) {
    try {
      const existingDraft: ElementDrafts = JSON.parse(existingDraftRaw);

      // Skip save if no changes (performance optimization)
      const isSame = isEqual(existingDraft.elementCreateEdit, new_element);
      if (isSame) return;

      updatedDrafts = {
        ...existingDraft,
        element: new_element,
        elementCreateEdit: new_element,
      };
    } catch (error) {
      console.error('Error parsing draft from localStorage', error);
      updatedDrafts = {
        id: new_element.id,
        section_id: storage_key,
        element: new_element,
        elementCreateEdit: new_element,
      };
    }
  } else {
    updatedDrafts = {
      id: new_element.id,
      section_id: storage_key,
      element: new_element,
      elementCreateEdit: new_element,
    };
  }

  // Save localStorage
  localStorage.setItem(draftKey, JSON.stringify(updatedDrafts));
  localStorage.setItem(editFlagKey, JSON.stringify(true));
};

export const syncPermissionSlots = (
  selectedTemplate: WritableDraft<Template> | null | undefined,
  updatedFields: Partial<TemplateElement>
) => {
  if (!selectedTemplate) return;

  const currentSlots = selectedTemplate.permission_slots ?? [];
  const currentSlotIds = new Set(currentSlots.map((slot) => slot.id));

  const extractSlots = (option?: Option[]): TemplateRoleTags[] => {
    return (
      option?.map(
        ({ value, placeholder }) =>
          ({
            id: value,
            name: placeholder,
          } as TemplateRoleTags)
      ) ?? []
    );
  };

  const newSlots = [
    ...extractSlots(updatedFields.permission_edit),
    ...extractSlots(updatedFields.permission_view),
  ].filter((slot) => !currentSlotIds.has(slot.id));

  if (newSlots.length > 0) {
    selectedTemplate.permission_slots = [...currentSlots, ...newSlots];
  }
};
